import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiFileText, FiVideo, FiSmile, FiInfo, FiCheck, FiAlertCircle, FiImage, FiX } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { API_URL, CATEGORIES, STORAGE_KEYS } from '../../utils/constants';
import { postsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import './Create.css';

const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const safePostSlug = (post) => {
  const base = slugify(post?.title) || 'post';
  if (post?.slug) return post.slug;
  if (post?.id) return `${base}-${post.id}`;
  return base;
};

const Create = () => {
  const [contentType, setContentType] = useState('article');

  const contentTypes = [
    { id: 'article', label: 'Artículo Científico', icon: <FiFileText />, description: 'Paper con normas APA' },
    { id: 'video', label: 'Video', icon: <FiVideo />, description: 'Horizontal o vertical (reel)' },
    { id: 'meme', label: 'Meme', icon: <FiSmile />, description: 'Imagen divertida' }
  ];

  return (
    <div className="create-page">
      <div className="create-container">
        <header className="create-header">
          <h1>Crear Nueva Publicación</h1>
          <p>Comparte tu conocimiento con la comunidad</p>
        </header>

        {/* Content Type Selector */}
        <div className="content-type-selector">
          {contentTypes.map(type => (
            <button
              key={type.id}
              className={`type-card ${contentType === type.id ? 'active' : ''}`}
              onClick={() => setContentType(type.id)}
            >
              <div className="type-icon">{type.icon}</div>
              <h3>{type.label}</h3>
              <p>{type.description}</p>
            </button>
          ))}
        </div>

        {/* Forms */}
        {contentType === 'article' && <ArticleForm />}
        {contentType === 'video' && <VideoForm />}
        {contentType === 'meme' && <MemeForm />}
      </div>
    </div>
  );
};

// Artículo Científico con asistencia
const ArticleForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const [formData, setFormData] = useState({
    // Metadatos
    title: '',
    authors: [{ name: user?.name || '', affiliation: '' }],
    category: '',
    keywords: [''],
    
    // Contenido principal con imágenes por sección
    abstract: '',
    abstractImages: [],
    introduction: '',
    introductionImages: [],
    theoreticalFramework: '',
    theoreticalFrameworkImages: [],
    methodology: '',
    methodologyImages: [],
    results: '',
    resultsImages: [],
    discussion: '',
    discussionImages: [],
    conclusions: '',
    conclusionsImages: [],
    references: [''],
    
    // Multimedia
    coverImage: null,
    coverImagePreview: null
  });

  const steps = [
    { id: 1, title: 'Metadatos', icon: <FiInfo /> },
    { id: 2, title: 'Resumen', icon: <FiFileText /> },
    { id: 3, title: 'Contenido', icon: <FiFileText /> },
    { id: 4, title: 'Referencias', icon: <FiFileText /> },
    { id: 5, title: 'Revisión', icon: <FiCheck /> }
  ];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addAuthor = () => {
    setFormData(prev => ({
      ...prev,
      authors: [...prev.authors, { name: '', affiliation: '' }]
    }));
  };

  const updateAuthor = (index, field, value) => {
    const newAuthors = [...formData.authors];
    newAuthors[index][field] = value;
    setFormData(prev => ({ ...prev, authors: newAuthors }));
  };

  const addKeyword = () => {
    setFormData(prev => ({
      ...prev,
      keywords: [...prev.keywords, '']
    }));
  };

  const updateKeyword = (index, value) => {
    const newKeywords = [...formData.keywords];
    newKeywords[index] = value;
    setFormData(prev => ({ ...prev, keywords: newKeywords }));
  };

  const addReference = () => {
    setFormData(prev => ({
      ...prev,
      references: [...prev.references, '']
    }));
  };

  const updateReference = (index, value) => {
    const newReferences = [...formData.references];
    newReferences[index] = value;
    setFormData(prev => ({ ...prev, references: newReferences }));
  };

  // Manejar imágenes por sección
  const handleSectionImages = (section, files) => {
    const imageKey = `${section}Images`;
    const newImages = Array.from(files).map(file => ({
      file,
      preview: URL.createObjectURL(file),
      caption: ''
    }));
    setFormData(prev => ({
      ...prev,
      [imageKey]: [...prev[imageKey], ...newImages]
    }));
  };

  const updateImageCaption = (section, index, caption) => {
    const imageKey = `${section}Images`;
    const newImages = [...formData[imageKey]];
    newImages[index].caption = caption;
    setFormData(prev => ({ ...prev, [imageKey]: newImages }));
  };

  const removeImage = (section, index) => {
    const imageKey = `${section}Images`;
    const newImages = formData[imageKey].filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, [imageKey]: newImages }));
  };

  const handleCoverImage = (file) => {
    if (file) {
      setFormData(prev => ({
        ...prev,
        coverImage: file,
        coverImagePreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current || isSubmitting) return;
    submitLockRef.current = true;
    setIsSubmitting(true);

    try {
      // Función auxiliar para subir imagen
      const uploadImage = async (file) => {
        const imageFormData = new FormData();
        imageFormData.append('file', file);
        const uploadRes = await fetch(`${API_URL}/media/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem(STORAGE_KEYS.TOKEN)}`
          },
          body: imageFormData
        });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          return uploadData?.media?.file_path || null;
        }
        return null;
      };

      // Subir imagen de portada si existe
      let coverImageUrl = null;
      if (formData.coverImage) {
        coverImageUrl = await uploadImage(formData.coverImage);
      }

      // Construir secciones con sus imágenes
      const sectionDefinitions = [
        { key: 'abstract', type: 'abstract', title: 'Resumen', allowImages: false },
        { key: 'introduction', type: 'intro', title: 'Introducción', allowImages: false },
        { key: 'theoreticalFramework', type: 'section', title: 'Marco Teórico', allowImages: true },
        { key: 'methodology', type: 'section', title: 'Metodología', allowImages: true },
        { key: 'results', type: 'section', title: 'Resultados', allowImages: true },
        { key: 'discussion', type: 'section', title: 'Discusión', allowImages: true },
        { key: 'conclusions', type: 'conclusion', title: 'Conclusiones', allowImages: false }
      ];

      const sections = [];
      
      for (const def of sectionDefinitions) {
        const content = formData[def.key];
        if (!content) continue;
        
        const sectionImages = [];
        const imageKey = `${def.key}Images`;
        
        // Solo subir imágenes para secciones que las permiten
        if (def.allowImages && formData[imageKey]?.length > 0) {
          for (let i = 0; i < formData[imageKey].length; i++) {
            const img = formData[imageKey][i];
            const imageUrl = await uploadImage(img.file);
            if (imageUrl) {
              sectionImages.push({
                image_url: imageUrl,
                caption: img.caption || `Figura ${i + 1}`,
                alt_text: img.caption || def.title,
                position: i + 1
              });
            }
          }
        }

        sections.push({
          section_type: def.type,
          title: def.title,
          content: content,
          images: sectionImages
        });
      }

      // Construir el objeto de datos completo
      const postData = {
        title: formData.title,
        type: 'article',
        category: formData.category,
        excerpt: formData.abstract.substring(0, 200),
        image: coverImageUrl,
        authors: formData.authors,
        keywords: formData.keywords.filter(k => k),
        // Campos legacy para compatibilidad
        abstract: formData.abstract,
        introduction: formData.introduction,
        theoretical_framework: formData.theoreticalFramework,
        methodology: formData.methodology,
        results: formData.results,
        discussion: formData.discussion,
        conclusions: formData.conclusions,
        references: formData.references.filter(r => r),
        // Nuevas secciones con imágenes
        sections: sections
      };

      // Crear el post con secciones
      const response = await fetch(`${API_URL}/posts/with-sections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(STORAGE_KEYS.TOKEN)}`
        },
        body: JSON.stringify(postData)
      });

      let data = null;
      try {
        data = await response.json();
      } catch (_) {
        // ignore non-json
      }

      if (!response.ok) {
        const backendMsg = data?.error || data?.message;
        throw new Error(backendMsg || 'Error al crear el artículo');
      }
      
      toast.success('¡Artículo publicado exitosamente!');
      navigate(`/article/${safePostSlug(data?.post)}`);
    } catch (error) {
      console.error('Error creating article:', error);
      toast.error(error?.message || 'Error al publicar el artículo');
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="article-form-container">
      {/* Progress Steps */}
      <div className="form-steps">
        {steps.map(step => (
          <div
            key={step.id}
            className={`step ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
            onClick={() => setCurrentStep(step.id)}
          >
            <div className="step-icon">{step.icon}</div>
            <span className="step-title">{step.title}</span>
          </div>
        ))}
      </div>

      <form className="article-form" onSubmit={handleSubmit}>
        {/* Step 1: Metadatos */}
        {currentStep === 1 && (
          <div className="form-step">
            <div className="step-header">
              <h2>Metadatos del Artículo</h2>
              <div className="help-box">
                <FiAlertCircle />
                <span>Información básica sobre tu paper científico</span>
              </div>
            </div>

            <div className="form-group">
              <label>Título del Artículo *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Máximo 12 palabras según normas APA"
                maxLength={120}
                required
              />
              <span className="help-text">El título debe ser claro, conciso y describir el contenido principal</span>
            </div>

            <div className="form-group">
              <label>Autor(es) *</label>
              <span className="help-text">El primer autor es tu nombre. Puedes cambiarlo o agregar coautores</span>
              {formData.authors.map((author, index) => (
                <div key={index} className="author-group">
                  <input
                    type="text"
                    value={author.name}
                    onChange={(e) => updateAuthor(index, 'name', e.target.value)}
                    placeholder="Nombre completo del autor"
                    required
                  />
                  <input
                    type="text"
                    value={author.affiliation}
                    onChange={(e) => updateAuthor(index, 'affiliation', e.target.value)}
                    placeholder="Institución o afiliación"
                    required
                  />
                </div>
              ))}
              <button type="button" className="btn-secondary" onClick={addAuthor}>
                + Agregar otro autor
              </button>
            </div>

            <div className="form-group">
              <label>Categoría *</label>
              <select
                value={formData.category}
                onChange={(e) => updateField('category', e.target.value)}
                required
              >
                <option value="">Selecciona una categoría</option>
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Palabras Clave *</label>
              <span className="help-text">3-5 palabras clave según normas APA</span>
              {formData.keywords.map((keyword, index) => (
                <input
                  key={index}
                  type="text"
                  value={keyword}
                  onChange={(e) => updateKeyword(index, e.target.value)}
                  placeholder={`Palabra clave ${index + 1}`}
                  required={index < 3}
                />
              ))}
              {formData.keywords.length < 5 && (
                <button type="button" className="btn-secondary" onClick={addKeyword}>
                  + Agregar palabra clave
                </button>
              )}
            </div>

            <div className="form-group">
              <label>Imagen de Portada</label>
              {formData.coverImagePreview && (
                <div className="cover-preview">
                  <img src={formData.coverImagePreview} alt="Portada" />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, coverImage: null, coverImagePreview: null }))}>
                    <FiX />
                  </button>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleCoverImage(e.target.files[0])}
              />
            </div>
          </div>
        )}

        {/* Step 2: Resumen */}
        {currentStep === 2 && (
          <div className="form-step">
            <div className="step-header">
              <h2>Resumen (Abstract)</h2>
              <div className="help-box">
                <FiAlertCircle />
                <span>150-250 palabras. Debe incluir: objetivo, método, resultados y conclusión</span>
              </div>
            </div>

            <div className="form-group">
              <label>Resumen/Abstract *</label>
              <textarea
                value={formData.abstract}
                onChange={(e) => updateField('abstract', e.target.value)}
                placeholder="Escribe un resumen conciso de tu investigación. Debe responder: ¿Qué investigaste? ¿Cómo lo hiciste? ¿Qué encontraste? ¿Qué significa?"
                rows={8}
                minLength={150}
                maxLength={2000}
                required
              />
              <span className="char-count">
                {formData.abstract.length} / 2000 caracteres
                {formData.abstract.length < 150 && ' (mínimo 150)'}
              </span>
            </div>
          </div>
        )}

        {/* Step 3: Contenido Principal */}
        {currentStep === 3 && (
          <div className="form-step">
            <div className="step-header">
              <h2>Contenido del Paper</h2>
              <div className="help-box">
                <FiAlertCircle />
                <span>Estructura estándar de un artículo científico. Puedes agregar imágenes a cada sección.</span>
              </div>
            </div>

            {/* Componente de sección reutilizable */}
            {[
              { key: 'introduction', label: '1. Introducción', help: 'Contexto, problema de investigación y objetivos', placeholder: 'Presenta el tema, la importancia del estudio y los objetivos de investigación...', allowImages: false },
              { key: 'theoreticalFramework', label: '2. Marco Teórico', help: 'Antecedentes y teorías relevantes', placeholder: 'Revisa estudios previos, teorías y conceptos fundamentales...', allowImages: true },
              { key: 'methodology', label: '3. Metodología', help: 'Diseño, participantes, instrumentos y procedimientos', placeholder: 'Describe cómo realizaste la investigación...', allowImages: true },
              { key: 'results', label: '4. Resultados', help: 'Presenta los hallazgos sin interpretarlos', placeholder: 'Reporta los datos obtenidos de forma objetiva...', allowImages: true },
              { key: 'discussion', label: '5. Discusión', help: 'Interpreta los resultados y compara con otros estudios', placeholder: 'Analiza e interpreta tus resultados...', allowImages: true },
              { key: 'conclusions', label: '6. Conclusiones', help: 'Síntesis, implicaciones y limitaciones', placeholder: 'Resume los hallazgos principales...', allowImages: false }
            ].map(section => (
              <div key={section.key} className="form-group section-with-images">
                <label>{section.label} *</label>
                <span className="help-text">{section.help}</span>
                <textarea
                  value={formData[section.key]}
                  onChange={(e) => updateField(section.key, e.target.value)}
                  placeholder={section.placeholder}
                  rows={6}
                  required
                />
                
                {/* Imágenes de la sección - solo para secciones que lo permiten */}
                {section.allowImages && (
                <div className="section-images">
                  <label className="images-label">
                    <FiImage /> Figuras para esta sección (opcional)
                  </label>
                  
                  {formData[`${section.key}Images`]?.length > 0 && (
                    <div className="images-preview">
                      {formData[`${section.key}Images`].map((img, idx) => (
                        <div key={idx} className="image-item">
                          <img src={img.preview} alt={`Figura ${idx + 1}`} />
                          <input
                            type="text"
                            placeholder="Descripción científica de la figura"
                            value={img.caption}
                            onChange={(e) => updateImageCaption(section.key, idx, e.target.value)}
                          />
                          <button 
                            type="button" 
                            className="remove-image"
                            onClick={() => removeImage(section.key, idx)}
                          >
                            <FiX />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleSectionImages(section.key, e.target.files)}
                    className="image-input"
                  />
                </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step 4: Referencias */}
        {currentStep === 4 && (
          <div className="form-step">
            <div className="step-header">
              <h2>Referencias Bibliográficas</h2>
              <div className="help-box">
                <FiAlertCircle />
                <span>Formato APA 7ª edición. Ejemplo: Apellido, A. A. (Año). Título. Editorial.</span>
              </div>
            </div>

            <div className="form-group">
              <label>Referencias en formato APA *</label>
              <span className="help-text">Ordena alfabéticamente por apellido del primer autor</span>
              {formData.references.map((ref, index) => (
                <textarea
                  key={index}
                  value={ref}
                  onChange={(e) => updateReference(index, e.target.value)}
                  placeholder={`Referencia ${index + 1}: Apellido, A. A. (2024). Título del trabajo. Editorial.`}
                  rows={2}
                  required={index === 0}
                />
              ))}
              <button type="button" className="btn-secondary" onClick={addReference}>
                + Agregar referencia
              </button>
              
              <div className="apa-examples">
                <h4>Ejemplos de formato APA:</h4>
                <ul>
                  <li><strong>Libro:</strong> García, M. (2023). Inteligencia Artificial. McGraw-Hill.</li>
                  <li><strong>Artículo:</strong> López, J. & Martínez, A. (2024). Machine Learning. <em>Science</em>, 45(2), 123-145.</li>
                  <li><strong>Web:</strong> OMS. (2024, enero 15). <em>Salud mental</em>. https://www.who.int</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Revisión */}
        {currentStep === 5 && (
          <div className="form-step">
            <div className="step-header">
              <h2>Revisión Final</h2>
              <div className="help-box success">
                <FiCheck />
                <span>Revisa toda la información antes de publicar</span>
              </div>
            </div>

            <div className="review-section">
              <h3>Metadatos</h3>
              <p><strong>Título:</strong> {formData.title || 'No especificado'}</p>
              <p><strong>Autores:</strong> {formData.authors.map(a => a.name).join(', ') || 'No especificado'}</p>
              <p><strong>Categoría:</strong> {formData.category || 'No especificada'}</p>
              <p><strong>Palabras clave:</strong> {formData.keywords.filter(k => k).join(', ') || 'No especificadas'}</p>
            </div>

            <div className="review-section">
              <h3>Estructura del Paper</h3>
              <ul className="checklist">
                <li className={formData.abstract ? 'complete' : ''}>
                  {formData.abstract ? <FiCheck /> : <FiAlertCircle />}
                  Resumen ({formData.abstract.length} caracteres)
                </li>
                <li className={formData.introduction ? 'complete' : ''}>
                  {formData.introduction ? <FiCheck /> : <FiAlertCircle />}
                  Introducción
                </li>
                <li className={formData.theoreticalFramework ? 'complete' : ''}>
                  {formData.theoreticalFramework ? <FiCheck /> : <FiAlertCircle />}
                  Marco Teórico
                </li>
                <li className={formData.methodology ? 'complete' : ''}>
                  {formData.methodology ? <FiCheck /> : <FiAlertCircle />}
                  Metodología
                </li>
                <li className={formData.results ? 'complete' : ''}>
                  {formData.results ? <FiCheck /> : <FiAlertCircle />}
                  Resultados
                </li>
                <li className={formData.discussion ? 'complete' : ''}>
                  {formData.discussion ? <FiCheck /> : <FiAlertCircle />}
                  Discusión
                </li>
                <li className={formData.conclusions ? 'complete' : ''}>
                  {formData.conclusions ? <FiCheck /> : <FiAlertCircle />}
                  Conclusiones
                </li>
                <li className={formData.references.filter(r => r).length > 0 ? 'complete' : ''}>
                  {formData.references.filter(r => r).length > 0 ? <FiCheck /> : <FiAlertCircle />}
                  Referencias ({formData.references.filter(r => r).length})
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="form-navigation">
          {currentStep > 1 && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setCurrentStep(prev => prev - 1)}
            >
              Anterior
            </button>
          )}
          
          {currentStep < 5 ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setCurrentStep(prev => prev + 1)}
            >
              Siguiente
            </button>
          ) : (
            <button 
              type="submit" 
              className="btn btn-success"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Publicando...' : 'Publicar Artículo'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

// Formulario de Video
const VideoForm = () => {
  const navigate = useNavigate();
  const [videoType, setVideoType] = useState('horizontal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    videoFile: null,
    thumbnail: null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current || isSubmitting) return;

    if (!formData.videoFile) {
      toast.error('Selecciona un video');
      return;
    }
    if (!formData.category) {
      toast.error('Selecciona una categoría');
      return;
    }

    setIsSubmitting(true);
    submitLockRef.current = true;
    try {
      const uploadFile = async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_URL}/media/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem(STORAGE_KEYS.TOKEN)}` },
          body: fd
        });
        if (!res.ok) throw new Error('Upload failed');
        const data = await res.json();
        return data?.media?.file_path || null;
      };

      const videoPath = await uploadFile(formData.videoFile);
      if (!videoPath) throw new Error('No se pudo subir el video');

      const thumbPath = formData.thumbnail ? await uploadFile(formData.thumbnail) : null;

      const apiType = videoType === 'vertical' ? 'video-reel' : 'video-horizontal';
      const payload = {
        title: formData.title,
        description: formData.description,
        content: formData.description,
        excerpt: (formData.description || '').slice(0, 200),
        type: apiType,
        category: formData.category,
        image: thumbPath,
        video_url: videoPath
      };

      const resp = await postsAPI.create(payload);
      toast.success('¡Video publicado exitosamente!');
      navigate(`/video/${safePostSlug(resp?.data?.post)}`);
    } catch (error) {
      console.error('Error creating video:', error);
      toast.error('Error al publicar el video');
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <form className="simple-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Tipo de Video *</label>
        <div className="radio-group">
          <label className="radio-option">
            <input
              type="radio"
              value="horizontal"
              checked={videoType === 'horizontal'}
              onChange={(e) => setVideoType(e.target.value)}
            />
            <span>Horizontal (16:9)</span>
            <small>Videos tradicionales, tutoriales, documentales</small>
          </label>
          <label className="radio-option">
            <input
              type="radio"
              value="vertical"
              checked={videoType === 'vertical'}
              onChange={(e) => setVideoType(e.target.value)}
            />
            <span>Vertical / Reel (9:16)</span>
            <small>Shorts, TikTok style, contenido rápido</small>
          </label>
        </div>
      </div>

      <div className="form-group">
        <label>Título del Video *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Título llamativo para tu video"
          required
        />
      </div>

      <div className="form-group">
        <label>Descripción</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Describe de qué trata tu video..."
          rows={4}
        />
      </div>

      <div className="form-group">
        <label>Categoría *</label>
        <select
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          required
        >
          <option value="">Selecciona una categoría</option>
          {CATEGORIES.map(cat => (
            <option key={cat.id} value={cat.id}>
              {cat.icon} {cat.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Archivo de Video *</label>
        <input
          type="file"
          accept="video/*"
          onChange={(e) => setFormData({ ...formData, videoFile: e.target.files[0] })}
          required
        />
        <span className="help-text">MP4, WebM, máximo 500MB</span>
      </div>

      <div className="form-group">
        <label>Miniatura (Opcional)</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setFormData({ ...formData, thumbnail: e.target.files[0] })}
        />
      </div>

      <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
        {isSubmitting ? 'Publicando...' : 'Publicar Video'}
      </button>
    </form>
  );
};

// Formulario de Meme
const MemeForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    imageFile: null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current || isSubmitting) return;

    if (!formData.imageFile) {
      toast.error('Selecciona una imagen');
      return;
    }
    if (!formData.category) {
      toast.error('Selecciona una categoría');
      return;
    }

    setIsSubmitting(true);
    submitLockRef.current = true;
    try {
      const fd = new FormData();
      fd.append('file', formData.imageFile);
      const up = await fetch(`${API_URL}/media/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem(STORAGE_KEYS.TOKEN)}` },
        body: fd
      });
      if (!up.ok) throw new Error('Upload failed');
      const upData = await up.json();
      const imgPath = upData?.media?.file_path;
      if (!imgPath) throw new Error('No se pudo subir la imagen');

      const payload = {
        title: formData.title,
        type: 'meme',
        category: formData.category,
        image: imgPath,
        excerpt: 'Meme',
        description: null,
        content: null
      };
      const resp = await postsAPI.create(payload);

      toast.success('¡Meme publicado exitosamente!');
      navigate(`/meme/${safePostSlug(resp?.data?.post)}`);
    } catch (error) {
      console.error('Error creating meme:', error);
      toast.error('Error al publicar el meme');
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <form className="simple-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Título del Meme *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Un título divertido..."
          required
        />
      </div>

      <div className="form-group">
        <label>Categoría *</label>
        <select
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          required
        >
          <option value="">Selecciona una categoría</option>
          {CATEGORIES.map(cat => (
            <option key={cat.id} value={cat.id}>
              {cat.icon} {cat.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Imagen del Meme *</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setFormData({ ...formData, imageFile: e.target.files[0] })}
          required
        />
        <span className="help-text">JPG, PNG, GIF, máximo 10MB</span>
      </div>

      <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
        {isSubmitting ? 'Publicando...' : 'Publicar Meme'}
      </button>
    </form>
  );
};

export default Create;
